import type { OptionSchema } from '@chartforge/shared-types';

/**
 * Options shared by all Charts by Nex Flow Automation charts, covering the PRD's initial
 * customization categories: appearance, fonts, spacing, layout, labels,
 * axes and data.
 *
 * `group` is the ChartConfig bucket the value is written into.
 */
export const commonSchemas: OptionSchema[] = [
  /* ---- Appearance & fonts ---- */
  {
    id: 'appearance.background',
    type: 'color',
    label: 'Background color',
    tier: 'trial',
    defaultValue: '#ffffff',
    group: 'appearance'
  },
  {
    id: 'appearance.border',
    type: 'color',
    label: 'Border color',
    tier: 'trial',
    defaultValue: '#e5e7eb',
    group: 'appearance'
  },
  {
    id: 'appearance.borderWidth',
    type: 'number',
    label: 'Border width',
    tier: 'trial',
    defaultValue: 1,
    group: 'appearance',
    validation: { min: 0, max: 8 }
  },
  {
    id: 'appearance.borderRadius',
    type: 'number',
    label: 'Border radius',
    tier: 'basic',
    defaultValue: 8,
    group: 'appearance',
    validation: { min: 0, max: 32 }
  },
  {
    id: 'appearance.colorScheme',
    type: 'enum',
    label: 'Color scheme',
    tier: 'basic',
    defaultValue: 'default',
    group: 'appearance',
    validation: { options: ['default', 'warm', 'cool', 'mono'] }
  },
  {
    id: 'font.family',
    type: 'font',
    label: 'Font family',
    tier: 'trial',
    defaultValue: 'Inter, system-ui, sans-serif',
    group: 'appearance'
  },
  {
    id: 'font.size',
    type: 'number',
    label: 'Font size',
    tier: 'trial',
    defaultValue: 12,
    group: 'appearance',
    validation: { min: 8, max: 32 }
  },
  {
    id: 'font.weight',
    type: 'enum',
    label: 'Font weight',
    tier: 'basic',
    defaultValue: '400',
    group: 'appearance',
    validation: { options: ['300', '400', '500', '600', '700'] }
  },

  /* ---- Spacing & layout ---- */
  {
    id: 'spacing.margin',
    type: 'spacing',
    label: 'Outer margin',
    tier: 'trial',
    defaultValue: 16,
    group: 'layout',
    validation: { min: 0, max: 96 }
  },
  {
    id: 'spacing.padding',
    type: 'spacing',
    label: 'Inner padding',
    tier: 'trial',
    defaultValue: 8,
    group: 'layout',
    validation: { min: 0, max: 64 }
  },
  {
    id: 'layout.showLegend',
    type: 'boolean',
    label: 'Show legend',
    tier: 'basic',
    defaultValue: true,
    group: 'layout'
  },

  /* ---- Labels ---- */
  {
    id: 'labels.title',
    type: 'label',
    label: 'Chart title',
    tier: 'trial',
    defaultValue: '',
    group: 'labels'
  },
  {
    id: 'labels.showTitle',
    type: 'boolean',
    label: 'Show title',
    tier: 'trial',
    defaultValue: false,
    group: 'labels'
  },
  {
    id: 'labels.titleSize',
    type: 'number',
    label: 'Title size',
    tier: 'basic',
    defaultValue: 16,
    group: 'labels',
    validation: { min: 10, max: 40 }
  },
  {
    id: 'labels.showValues',
    type: 'boolean',
    label: 'Show value labels',
    tier: 'trial',
    defaultValue: false,
    group: 'labels'
  },
  {
    id: 'labels.size',
    type: 'number',
    label: 'Value label size',
    tier: 'basic',
    defaultValue: 11,
    group: 'labels',
    validation: { min: 8, max: 24 }
  },
  {
    id: 'labels.color',
    type: 'color',
    label: 'Value label color',
    tier: 'basic',
    defaultValue: '#374151',
    group: 'labels'
  },

  /* ---- Axes & gridlines ---- */
  {
    id: 'axes.x.visible',
    type: 'boolean',
    label: 'Show X axis',
    tier: 'trial',
    defaultValue: true,
    group: 'axes'
  },
  {
    id: 'axes.y.visible',
    type: 'boolean',
    label: 'Show Y axis',
    tier: 'trial',
    defaultValue: true,
    group: 'axes'
  },
  {
    id: 'axes.x.title',
    type: 'label',
    label: 'X axis title',
    tier: 'basic',
    defaultValue: '',
    group: 'axes'
  },
  {
    id: 'axes.y.title',
    type: 'label',
    label: 'Y axis title',
    tier: 'basic',
    defaultValue: '',
    group: 'axes'
  },
  {
    id: 'axes.gridlines',
    type: 'boolean',
    label: 'Show gridlines',
    tier: 'trial',
    defaultValue: true,
    group: 'axes'
  },
  {
    id: 'axes.gridColor',
    type: 'color',
    label: 'Gridline color',
    tier: 'basic',
    defaultValue: '#e5e7eb',
    group: 'axes'
  },
  {
    id: 'axes.axisColor',
    type: 'color',
    label: 'Axis color',
    tier: 'basic',
    defaultValue: '#6b7280',
    group: 'axes'
  },

  /* ---- Data ---- */
  {
    id: 'data.sortCategories',
    type: 'enum',
    label: 'Sort categories by value',
    tier: 'pro',
    defaultValue: 'none',
    group: 'data',
    validation: { options: ['none', 'ascending', 'descending'] }
  },
  {
    id: 'data.valueFormat',
    type: 'enum',
    label: 'Value format',
    tier: 'basic',
    defaultValue: 'default',
    group: 'data',
    validation: { options: ['default', 'compact', 'percent'] }
  }
];
